/**
 * In-flight request de-duplication.
 *
 * Collapses identical CONCURRENT reads into a single network request: callers
 * that ask for the same thing while a request is already in flight share its
 * promise instead of issuing their own.
 *
 * Why this exists (defence in depth, not a bug fix): production is
 * `clinic.kokh-vet.com` → `admin.kokh-vet.com`, so every API call is
 * cross-origin and carries its own CORS preflight — roughly double the HTTP
 * round-trips — against a synchronous gunicorn pool where each in-flight
 * request occupies a worker. A handful of tabs emitting duplicate concurrent
 * reads can starve the pool and make every user see timeouts that look like
 * unrelated backend slowness.
 *
 * ⚠️ It deliberately does NOT hide the class of bug found on 2026-07-26: a leaked
 * timer that re-fires SEQUENTIALLY after each response never has two requests in
 * flight at once, so it passes straight through. That property is the point — a
 * bound that masked such a loop would have cost us the diagnosis.
 */
const inFlight = new Map<string, Promise<unknown>>();

export function dedupeInFlight<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;

  if (existing)
    return existing;

  const pending = run().finally(() => {
    // Released on settle, so this only ever shares a CONCURRENT request. It is
    // not a response cache and must never become one — callers rely on getting
    // fresh data on a fresh call.
    inFlight.delete(key);
  });

  inFlight.set(key, pending);

  return pending;
}

/** Test/diagnostic helper: how many distinct requests are in flight right now. */
export function inFlightCount() {
  return inFlight.size;
}
